import { IsInt, IsOptional, IsPositive, IsString, IsUUID, IsEmail, IsNotEmpty, ArrayNotEmpty, IsObject, ValidateNested } from 'class-validator';
import { Type } from 'class-transformer';
import { EventPriceDto } from './event-price.dto';
import { OrderDto } from './order.dto';
import { EventInstanceDto } from './event.dto';
import { UserDto } from './user.dto';
import { DiscountDto } from './discount.dto';
import { IntakeFormUserDataResults } from './intake-form-settings.dto';

export class EventTicketDto {
	public id: string;
	public shortId: string;
	public orderId: string;
	public eventPriceId: string;
	public userId: string;
	public signedAt: Date;
	public isTicketUsed: boolean;
	public isCanceled: boolean;
	public intakeFormResults?: IntakeFormUserDataResults;
	public fullName?: string;
	public email?: string;
	public isUsedByUserId?: string;
	public usedAt?: Date;
	public discountId?: string;
	public createdAt: Date;
	public updatedAt: Date;
	public deletedAt?: Date;

	public user?: UserDto;
	public order?: OrderDto;
	public eventPrice?: EventPriceDto;
	public usedByUser?: UserDto;
	public discount?: DiscountDto;
}

interface ICreateEventTicketDto {
	eventPriceId: string;
	quantity: number;
}

export class CreateEventTicketDto implements ICreateEventTicketDto {
	@IsString()
	@IsUUID()
	public eventPriceId: string;

	@IsInt()
	@IsPositive()
	public quantity: number;

	@IsOptional()
	@IsUUID()
	public discountId?: string;

	@IsOptional()
	@IsString()
	public fullName?: string;

	@IsOptional()
	@IsEmail()
	public email?: string;
}

export class CompactTicket implements ICreateEventTicketDto {
	public eventPriceId: string;
	public quantity: number;

	public discountId?: string;
	public discountAmount?: number;

	public pricePerTicket?: number;
	public sumPrice?: number;

	public eventPrice?: EventPriceDto;
}

export class EventRevenueStatsDto {
	totalRevenue: number;
	thisDayRevenue: number;
	thisWeekRevenue: number;
	thisMonthRevenue: number;
	prevEventRevenue: number;
	comparedIncome: number;
}

export class EditEventTicketDto {
	@IsOptional()
	id?: string;

	@IsUUID()
	eventId: string;

	@IsOptional()
	intakeFormResults?: IntakeFormUserDataResults;

	// updated price tier does not affect the order paid amount
	@IsOptional()
	@IsUUID()
	eventPriceId?: string;

	@IsOptional()
	isTicketUsed?: boolean;

	@IsOptional()
	usedAt?: Date;

	@IsOptional()
	isUsedByUserId?: string;
}

export class PublicEditEventTicketDto {
	intakeFormResults?: IntakeFormUserDataResults;
}

export class EventPriceTicketsCountDto {
  public basePriceId: string | null;
	public ticketsCount: number;
}

export class EventPriceSpotsCountDto {
  public basePriceId: string | null;
	public spotsCount: number;
}

export class PriceLimit {
	basePriceId: string | null;
	priceLimit: number;
}

export class EventTicketsTotalsDto {
	public totalTicketsCount: number;
	public pricesTicketsCount: EventPriceTicketsCountDto[];
	public ticketsLimit: number;
	public pricesLimits: PriceLimit[];
	public allSpotsCount: number;
	public pricesSpots: EventPriceSpotsCountDto[];
}

export class EventTicketsTotalsWithReservationsDto extends EventTicketsTotalsDto {
	public totalTicketsCountWithReservations: number;
	public allSpotsCountWithReservations: number;
	public override pricesTicketsCount: (EventPriceTicketsCountDto & { ticketsCountWithReservations: number })[];
	public override pricesSpots: (EventPriceSpotsCountDto & { spotsCountWithReservations: number; })[];
}

export class EventTicketsReservationCacheDataDto {
	public event: EventInstanceDto;
	public reservations: EventTicketsReservationDto[];
	public confirmedTicketsTotals: EventTicketsTotalsDto;
}

export class EventTicketsReservationDto {
	public userIpAndAgent: string;
	public baseEventPriceId: string | null;
	public reservedAt: number;
	public expiresAt: number;
}

export class UpsertEventTicketsReservationDto {
	public baseEventPriceId: string | null;
}

export class AddManyAttendeesManuallyDto {
	@IsNotEmpty()
	@ArrayNotEmpty({ message: 'At least one attendee is required' })
	@ValidateNested({ each: true })
	@Type(() => AddAttendeeManuallyDto)
	public attendees: AddAttendeeManuallyDto[];
}

export class AddAttendeeManuallyDto {
	@IsOptional()
	@IsNotEmpty({ message: 'Price tier is required' })
	@ArrayNotEmpty({ message: 'Price tier is required' })
	@IsString({ each: true })
	@IsUUID(undefined, { each: true })
	public basePriceTierIds?: string[];

	@IsNotEmpty()
	@IsObject()
	@ValidateNested()
	@Type(() => IntakeFormUserDataResults)
	public intakeFormResults: IntakeFormUserDataResults;
}

export class TicketWithoutPaymentDetailsDto
	implements
		Pick<
			EventTicketDto,
			| 'id'
			| 'shortId'
			| 'orderId'
			| 'userId'
			| 'eventPriceId'
			| 'isTicketUsed'
			| 'isCanceled'
			| 'intakeFormResults'
			| 'fullName'
			| 'email'
			| 'isUsedByUserId'
			| 'usedAt'
			| 'order'
			| 'usedByUser'
			| 'eventPrice'
			| 'signedAt'
		>
{
	id: string;
	shortId: string;
	orderId: string;
	userId: string;
	signedAt: Date;
	eventPriceId: string;
	isTicketUsed: boolean;
	isCanceled: boolean;
	intakeFormResults?: IntakeFormUserDataResults;
	fullName?: string;
	email?: string;
	isUsedByUserId?: string;
	usedAt?: Date;
	eventPrice?: EventPriceDto;

	// compatibility with EventTicketDto
	order?: OrderDto;
	discount?: DiscountDto;
	usedByUser?: UserDto;
}
